<template>
  <div class="overflow-list" :data-testid="dataTestid">
    <template v-if="normalizedItems.length">
      <span
        v-for="(item, index) in visibleItems"
        :key="`visible-${index}`"
        class="overflow-list__visible"
      >
        <slot name="primary" :item="item" :index="index">
          <span class="overflow-list__primary">{{ itemLabel(item) }}</span>
        </slot>
      </span>
      <v-tooltip
        v-if="overflowCount > 0"
        ref="overflowTooltip"
        eager
        transition="fade-transition"
        :bottom="position !== 'top'"
        :top="position === 'top'"
        :fixed="fixed"
        :content-class="tooltipContentClass"
        @input="repositionOverflowTooltip"
      >
        <template #activator="{ on, attrs }">
          <button
            v-bind="attrs"
            type="button"
            class="overflow-list__badge"
            :data-testid="`${dataTestid}-badge`"
            :aria-label="overflowBadgeLabel"
            v-on="on"
            @click.stop
          >
            +{{ overflowCount }}
          </button>
        </template>
        <div class="overflow-list__tooltip" :data-testid="`${dataTestid}-tooltip`">
          <div
            v-for="(line, idx) in tooltipLines"
            :key="idx"
            class="overflow-list__tooltip-line"
            :data-testid="`${dataTestid}-tooltip-line--${idx}`"
          >
            {{ line }}
          </div>
        </div>
      </v-tooltip>
    </template>
    <span v-else class="overflow-list__empty">{{ emptyText }}</span>
  </div>
</template>

<script>
import {
  filterOverflowItems,
  formatOverflowTooltipLines,
  toOverflowLabel,
} from './overflowListUtils';
import { repositionVTooltip } from '@/views/common/tooltipPosition';

export default {
  name: 'OverflowList',

  props: {
    /** Full list. Strings, or objects with `name`. Blanks are dropped. */
    items: {
      type: Array,
      default: () => [],
    },
    emptyText: {
      type: String,
      default: '—',
    },
    /** `bottom` (default) or `top`. Passed to v-tooltip. */
    position: {
      type: String,
      default: 'bottom',
      validator: (value) => ['bottom', 'top'].includes(value),
    },
    /** Set when a parent overflow clip (tables) would hide the tooltip. */
    fixed: {
      type: Boolean,
      default: false,
    },
    /** Extra classes merged onto the tooltip content element. */
    contentClass: {
      type: String,
      default: '',
    },
    dataTestid: {
      type: String,
      default: 'overflow-list',
    },
    /** How many labels render inline before the +N badge. */
    visibleCount: {
      type: Number,
      default: 1,
      validator: (value) => Number.isInteger(value) && value >= 1,
    },
  },

  computed: {
    normalizedItems() {
      return filterOverflowItems(this.items);
    },
    visibleItems() {
      return this.normalizedItems.slice(0, this.visibleCount);
    },
    overflowCount() {
      return Math.max(this.normalizedItems.length - this.visibleCount, 0);
    },
    overflowBadgeLabel() {
      const noun = this.overflowCount === 1 ? 'item' : 'items';
      return `Show ${this.overflowCount} more ${noun}`;
    },
    tooltipLines() {
      return formatOverflowTooltipLines(this.normalizedItems.slice(this.visibleCount));
    },
    tooltipContentClass() {
      const placement = this.position === 'top' ? 'tooltip-top' : 'tooltip-bottom';
      if (!this.contentClass) return placement;
      return `${placement} ${this.contentClass}`;
    },
  },

  methods: {
    itemLabel(item) {
      return toOverflowLabel(item);
    },
    repositionOverflowTooltip(isOpen) {
      if (!isOpen) return;
      repositionVTooltip(this.$refs.overflowTooltip);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './OverflowList.scss';
</style>
